<template>
  <div>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="errorMessage ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ errorMessage }}
        </p>
      </div>
    </b-alert>

    <div v-if="!loading && !errorMessage">
      <div class="d-flex align-items-center mb-1">
        <div>
          <b-badge
            variant="light-danger"
            class="mr-1 filter-chip d-inline-flex align-items-center"
            :class="{ active: activeFilters.includes('missing') }"
            @click="toggleFilter('missing')"
          >
            <b-form-checkbox
              type="checkbox"
              :checked="activeFilters.includes('missing') "
              style="pointer-events: none"
            />
            Missing ({{ statusCounts.missing }})
          </b-badge>

          <b-badge
            variant="light-warning"
            class="mr-1 filter-chip d-inline-flex align-items-center "
            :class="{ active: activeFilters.includes('extra') }"
            @click="toggleFilter('extra')"
          >
            <b-form-checkbox
              type="checkbox"
              :checked="activeFilters.includes('extra') "
              style="pointer-events: none"
            />

            Extra ({{ statusCounts.extra }})
          </b-badge>

          <b-badge
            variant="light-warning"
            class="mr-1 filter-chip d-inline-flex align-items-center "
            :class="{ active: activeFilters.includes('p. filled') }"
            @click.prevent="toggleFilter('p. filled')"
          >
            <b-form-checkbox
              type="checkbox"
              :checked="activeFilters.includes('p. filled') "
              style="pointer-events: none"
            />
            Partially Filled ({{ statusCounts['p. filled'] }})
          </b-badge>

          <b-badge
            variant="light-success"
            class="mr-1 filter-chip d-inline-flex align-items-center "
            :class="{ active: activeFilters.includes('fulfilled')}"
            @click.prevent="toggleFilter('fulfilled')"
          >
            <b-form-checkbox
              type="checkbox"
              :checked="activeFilters.includes('fulfilled') "
              style="pointer-events: none"
            />
            Fulfilled ({{ statusCounts.fulfilled }})
          </b-badge>

          <b-badge
            variant="light-primary"
            class="filter-chip d-inline-flex align-items-center"
            :class="{ active: activeFilters.length === 0 }"
            @click="activeFilters = []"
          >
            <b-form-checkbox
              type="checkbox"
              :checked="activeFilters.length === 0"
              style="pointer-events: none"
            />
            All ({{ records.length }})
          </b-badge>
        </div>

        <div>
          <b-button
            variant="outline-primary"
            :disabled="(!includeRemainingItems && selectedIds.length === 0) || downloading"
            class="mx-1"
            @click="downloadReport"
          >
            <feather-icon
              v-if="!downloading"
              v-b-tooltip.hover="{boundary:'window'}"
              icon="DownloadIcon"
              title="Download"
            />
            <b-spinner
              v-else
              label="Small Spinner"
              small
            />
          </b-button>

          <b-button
            variant="outline-primary"
            :disabled="(!includeRemainingItems && selectedIds.length === 0) || sendingEmail"
            class="mr-1"
            @click="sendReportEmail"
          >
            <feather-icon
              v-if="!sendingEmail"
              v-b-tooltip.hover="{boundary:'window'}"
              icon="SendIcon"
              title="Send Email"
            />
            <b-spinner
              v-else
              label="Small Spinner"
              small
            />
          </b-button>
        </div>

        <div
          class="d-flex align-items-center ml-auto"
          style="width: 40%;"
        >
          <b-form-input
            v-model="bulkRemark"
            placeholder="Enter remark"
            class="mr-1"
          />
          <b-button
            variant="outline-primary"
            :disabled="!selectedIds.length"
            @click="updateBulkRemarks"
          >
            Update Bulk Remarks
          </b-button>
        </div>
      </div>

      <b-table
        :key="activeFilters.join('-')"
        :items="filteredRecords"
        :fields="columns"
        responsive="sm"
        striped
        hover
        class="compact-table1"
        :tbody-tr-class="rowClass"
        show-empty
        empty-text="No records found"
      >
        <template #head(select)>
          <b-form-checkbox
            :checked="isAllSelected"
            @change="toggleSelectAll"
          /></template>

        <template #cell(select)="row">
          <b-form-checkbox
            v-model="selectedIds"
            :value="row.item.po_item_id"
          /></template>

        <template #cell(fullfilled_qty)="row">
          {{ row.item.ordered_qty - row.item.remaining_qty }}
        </template>

        <template #cell(matches)="row">
          <li
            v-for="(item, index) in row.item.matches"
            :key="index"
            style="list-style-type: none;"
          >
            {{ item.invoice_number }} - {{ item.matched_qty }}/{{ item.total_qty }}
            <span v-if="item.remaining_qty">(R - {{ item.remaining_qty }})</span>
          </li>
        </template>

        <template #cell(status)="row">
          <b-badge :variant="statusVariant(row.item.status)">
            {{ row.item.status.toUpperCase() }}
          </b-badge>
        </template>

        <template #cell(remark)="row">
          <remark-form
            :po-item-id="row.item.po_item_id"
            :remark="row.item.remark"
            @remarksUpdated="handleUpdateRemarks"
          />
        </template>

      </b-table>

      <h6>Remaining Invoice Items </h6>
      <b-form-checkbox
        v-model="includeRemainingItems"
        type="checkbox"
        :disabled="invoiceRecords.length === 0"
        class="mb-1"
      >
        Include Remaining Invoice Items in PDF report / Email
      </b-form-checkbox>
      <b-table
        :items="invoiceRecords"
        :fields="invoiceRecordsColumns"
        responsive="sm"
        striped
        hover
        class="compact-table"
        :tbody-tr-class="rowClass2"
        show-empty
        empty-text="No records found"
      >
        <template #cell(invoice_date)="row">
          {{ formatedDate(row.item.invoice_date) }}
        </template>
      </b-table>
    </div>

  </div>

</template>

<script>
import {
  BTable, BAlert, BSpinner,
  BBadge,
  BFormInput,
  BButton,
  BFormCheckbox,
  VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import download from 'downloadjs'
import dateMixin from '@/mixins/dateMixin'
import RemarkForm from './RemarkForm.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BTable, BAlert, BSpinner, BBadge, BFormInput, BButton, BFormCheckbox, RemarkForm,
  },
  mixins: [dateMixin],
  props: {
    poId: { type: Number, required: true },
  },
  data() {
    return {
      loading: true,
      errorMessage: '',
      records: [],
      columns: [
        { key: 'select', label: '' },
        { key: 'product', label: 'Product', sortable: false },
        { key: 'ordered_qty', label: 'PO Qty', sortable: false },
        { key: 'fullfilled_qty', label: 'Fullfilled Qty', sortable: false },
        { key: 'remaining_qty', label: 'Remaining Qty', sortable: false },
        { key: 'matches', label: 'Matches', sortable: false },
        {
          key: 'status', label: 'Status', sortable: false,
        },
        {
          key: 'remark', label: 'Remark', sortable: false, thStyle: { width: '40%' },
        },
      ],
      invoiceRecords: [],
      invoiceRecordsColumns: [
        { key: 'invoice_number', label: 'Invoice', sortable: false },
        { key: 'invoice_date', label: 'Invoice Date', sortable: false },
        { key: 'product', label: 'Product', sortable: false },
        { key: 'total_qty', label: 'Invoice Qty', sortable: false },
        { key: 'matched_qty', label: 'Matched Qty', sortable: false },
        { key: 'remaining_qty', label: 'Remaining Qty', sortable: false },
      ],
      activeFilters: [],
      selectedIds: [],
      bulkRemark: null,
      downloading: false,
      includeRemainingItems: false,
      sendingEmail: false,
    }
  },
  computed: {
    statusCounts() {
      const counts = {
        missing: 0, 'p. filled': 0, extra: 0, fulfilled: 0,
      }

      this.records.forEach(r => {
        const { status } = r
        counts[status] += 1
      })

      return counts
    },
    filteredRecords() {
      let items = this.records

      if (this.activeFilters.length) {
        items = items.filter(i => this.activeFilters.includes(i.status))
      }

      return items
    },
    isAllSelected() {
      return this.filteredRecords.length > 0
      && this.filteredRecords.every(r => this.selectedIds.includes(r.po_item_id))
    },

  },
  created() {
    this.fetchDetails()
  },
  methods: {
    async fetchDetails() {
      try {
        this.loading = true
        const { data } = await axios.get(`/po_invoice_matching/po-items-match-details/${this.poId}`)

        // Add status field in each record
        const records = data.po_items_matches.map(r => ({
          ...r,
          status: this.computeStatus(r),
          hasRemainingInvoiceQty: r.matches?.some(m => m.remaining_qty > 0),
          rowClass: this.computeRowClass(r),
        }))

        this.records = this.sortItems(records)

        this.invoiceRecords = data.remaining_invoice_items
        if (this.invoiceRecords.length > 0) {
          this.includeRemainingItems = true
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.detail || 'Failed to fetch details'
      } finally {
        this.loading = false
      }
    },
    statusVariant(status) {
      if (status === 'missing') return 'danger'
      if (status === 'p. filled') return 'warning'
      if (status === 'extra') return 'warning'
      return 'success'
    },
    computeStatus(item) {
      if (item.remaining_qty === item.ordered_qty) return 'missing'
      if (item.remaining_qty > 0) return 'p. filled'

      const hasExtra = item.matches?.some(m => m.remaining_qty > 0)
      if (hasExtra) return 'extra'

      return 'fulfilled'
    },
    sortItems(items) {
      const order = {
        missing: 1,
        extra: 2,
        'p. filled': 3,
        fulfilled: 4,
      }

      return items.sort(
        (a, b) => order[a.status] - order[b.status],
      )
    },
    async downloadReport() {
      try {
        this.downloading = true
        const response = await axios.post('/po_invoice_matching/download_po_items_match_report/', {
          po_id: this.poId,
          po_item_ids: this.selectedIds,
          include_remaining_items: this.includeRemainingItems,
        }, {
          responseType: 'blob',
        })

        const contentType = response.headers['content-type']
        download(response.data, `PO Items Match Report - ${this.poId}`, contentType)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.detail || 'Failed to download report',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.downloading = false
      }
    },
    async sendReportEmail() {
      try {
        this.sendingEmail = true
        const response = await axios.post('/po_invoice_matching/email_po_items_match_report/', {
          po_id: this.poId,
          po_item_ids: this.selectedIds,
          include_remaining_items: this.includeRemainingItems,
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: response?.data?.detail || 'Email Sent',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.detail || 'Failed to send email',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.sendingEmail = false
      }
    },
    toggleFilter(status) {
      const index = this.activeFilters.indexOf(status)

      if (index > -1) {
        this.activeFilters.splice(index, 1)
      } else {
        this.activeFilters.push(status)
      }

      this.selectedIds = [] // Clear selection
    },
    computeRowClass(item) {
      if (!item) return ''

      if (item.hasRemainingInvoiceQty) return 'table-success'

      if (item.remaining_qty > 0) return 'table-danger' // Any qty pending

      return ''
    },
    rowClass(item) {
      return item?.rowClass || ''
    },
    rowClass2(item) {
      if (!item) return ''
      if (item.remaining_qty > 0) return 'table-success' // Any qty remaining in invoice
      return ''
    },
    handleUpdateRemarks(poItemIDs, updatedRemark) {
      this.records = this.records.map(record => {
        if (poItemIDs.includes(record.po_item_id)) {
          return { ...record, remark: updatedRemark }
        }
        return record
      })
    },
    toggleSelectAll(isChecked) {
      if (isChecked) {
        this.selectedIds = this.filteredRecords.map(r => r.po_item_id)
      } else {
        this.selectedIds = []
      }
    },
    async updateBulkRemarks() {
      if (!this.selectedIds.length) return

      try {
        await axios.post('/update_po_item_remarks', {
          po_item_ids: this.selectedIds,
          remark: this.bulkRemark,
        })

        // update UI instantly
        this.handleUpdateRemarks(this.selectedIds, this.bulkRemark)

        // reset
        this.selectedIds = []
        this.bulkRemark = ''
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.error || 'Bulk update failed',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.filter-chip {
  cursor: pointer;
}

.filter-chip.active {
  border: 2px solid;
}
</style>
